/**
 * 精算で金券を会計に当てる順と、会計に当たらない金券の判定（ODR-1724）。
 *
 * バックエンド `PaymentsLogic::sortCouponsForApply` / `applyCoupons` と同じ当て方:
 *  1) お釣りなしを先に当てる（会計を超える分は切り捨て）
 *  2) 残りにお釣りありを当てる（超える分はお釣り）
 *  3) 残りを現金
 * 当てる時点で会計の残りが0円の金券は使われないので、精算させない（サーバーもエラーを返す）。
 *
 * DOM や Vue インスタンスに依存しないため、そのままユニットテストできる。
 */

export interface ApplyCoupon {
  coupon_name: string;
  coupon_price: number | string;
  coupon_is_change: boolean;
}

/**
 * 金券を当てる順（お釣りなし → お釣りあり）に並べる。同じ種別の中の順は変えない。
 *
 * @param coupons 選択された金券（同じ金券の複数枚を含む）
 * @return 並べ直した新しい配列
 */
export function sortCouponsForApply<T extends ApplyCoupon>(coupons: T[]): T[] {
  return [
    ...coupons.filter((coupon) => !coupon.coupon_is_change),
    ...coupons.filter((coupon) => coupon.coupon_is_change),
  ];
}

/**
 * 会計に当たらない金券を返す（当てる順に並べたうえで、当てる時点で残りが0円のもの）。
 *
 * @param totalPrice 値引き後の会計額
 * @param coupons 選択された金券（未選択の行は除いて渡す）
 * @return 当たらない金券。無ければ空配列
 */
export function findUnusedCoupons<T extends ApplyCoupon>(
  totalPrice: number,
  coupons: T[]
): T[] {
  let remaining = totalPrice;
  const unused: T[] = [];
  for (const coupon of sortCouponsForApply(coupons)) {
    if (remaining <= 0) {
      unused.push(coupon);
      continue;
    }
    remaining = Math.max(0, remaining - Number(coupon.coupon_price));
  }
  return unused;
}

/**
 * 当たらない金券があるときに精算ボタンの近くへ出すメッセージ。
 *
 * @param unused findUnusedCoupons の結果
 * @return メッセージ。当たらない金券が無ければ空文字
 */
export function unusedCouponMessage(unused: ApplyCoupon[]): string {
  if (unused.length === 0) {
    return "";
  }
  const names = Array.from(new Set(unused.map((coupon) => coupon.coupon_name)));
  return `会計に当たらない金券（${names.join("、")}）が含まれています。金券を外してから精算してください。`;
}
